// ▶ O FUNIL UNICO por onde a embalagem sai de um produto. Ninguem le
// `unidades_por_embalagem` nem `unidade` direto -- e a mesma decisao de
// regraDoPreco() em pedidos/regraQuantidade.ts, e pela mesma razao:
//
// a migration 0021 criou as colunas em 28/08/2026, mas o sync e POR CURSOR
// (`.gt('atualizado_em', cursor)`) e linha que nao mudou nunca mais e puxada.
// Todo aparelho com produto em cache de antes disso le `undefined`, e
// `undefined !== null` e VERDADEIRO. Em 18/08 essa mesma armadilha derrubou a
// tela do pedido inteira.
export interface Embalagem {
  // Nulo quando o produto NAO tem embalagem fechada. `1` vira nulo aqui: e dado
  // real (19 produtos da MAVALERIO, o saco de 30 kg), e significa que a
  // embalagem E a unidade -- nao ha o que dividir nem segundo campo a mostrar.
  fator: number | null
  nome: string
}

export function embalagemDoProduto(
  produto: { unidade?: string | null; unidades_por_embalagem?: number | null },
): Embalagem {
  const bruto = produto.unidades_por_embalagem ?? null
  const fator = bruto !== null && bruto > 1 ? bruto : null
  // ⚠ Achado dela em 08/09: sem embalagem fechada, nome vazio e mesmo "un" --
  // e a unidade, uma a uma. COM embalagem fechada (fator > 1), "un" mentiria
  // ("un (24 un)" le como se 1 unidade valesse 24 unidades) -- o fallback
  // vira "cx", o termo generico que ela mesma usa para descrever uma caixa
  // fechada. Nao mexe no campo `unidade` cadastrado (que segue livre, 172
  // produtos usam "dz"/"½ dz" -- ver medir-banco-antes-de-travar-campo-livre);
  // isto so troca o QUE MOSTRAR quando ele esta vazio.
  const nome = (produto.unidade ?? '').trim() || (fator !== null ? 'cx' : 'un')
  return { fator, nome }
}

// Pedido dela 01/10: o rotulo do preco diz DE QUE e o preco -- "Preço (CX)",
// "Preço (FD)", "Preço (DZ)"; a unidade solta (UN, UN., UND, vazio) e "Preço UND".
export function rotuloDoPreco(
  produto: { unidade?: string | null; unidades_por_embalagem?: number | null },
): string {
  const tipo = embalagemDoProduto(produto).nome.trim().toUpperCase().replace(/\.$/, '')
  return ['UN', 'UND', 'UNID'].includes(tipo) ? 'Preço UND' : `Preço (${tipo})`
}

// Regra dela 01/10 (card vermelho da tabela): sem tipo de embalagem, ou CX/FD
// sem Qtd por Emb. "UN." e "DZ" sem Qtd estao certos (medido 01/10: exigir Qtd
// de todos pintaria 86%). A MESMA regra pinta o campo na tela Editar produto.
export function faltaDeEmbalagem(
  produto: { unidade?: string | null; unidades_por_embalagem?: number | null },
): 'tipo' | 'qtd' | null {
  const tipo = (produto.unidade ?? '').trim().toUpperCase().replace(/\.$/, '')
  if (tipo === '') return 'tipo'
  return (tipo === 'CX' || tipo === 'FD') && !temEmbalagemFechada(produto) ? 'qtd' : null
}

export function temEmbalagemFechada(
  produto: { unidade?: string | null; unidades_por_embalagem?: number | null },
): boolean {
  return embalagemDoProduto(produto).fator !== null
}

// ▶ O funil do item VENDIDO -- e nao do produto vivo. A spec e explicita: "se a
// fabrica trocar a caixa de 20 para 24, o PDF de um pedido antigo nao pode se
// reescrever sozinho". Por isso `itens_pedido.unidades_por_embalagem` congela o
// fator no momento da venda, e quem le um item JA VENDIDO tem de vir por aqui.
//
// So o FATOR congela; o nome (`CX`, `dz`) continua vindo do produto, porque o
// item nao guarda o nome. Item gravado antes da 0021 le `undefined` e vira
// `null` -- que e o certo: pedido antigo nao tinha embalagem fechada, e cair no
// produto vivo ressuscitaria exatamente a reescrita que a spec proibe.
export function embalagemDoItem(
  item: { unidades_por_embalagem?: number | null },
  // Recebe o produto INTEIRO e ignora o fator dele de proposito: dali sai so o
  // nome. Se um dia alguem "consertar" isto caindo no produto quando o item nao
  // tem fator, o PDF do pedido antigo volta a se reescrever -- ha teste preso.
  produto: { unidade?: string | null; unidades_por_embalagem?: number | null },
): Embalagem {
  return embalagemDoProduto({
    unidade: produto.unidade,
    unidades_por_embalagem: item.unidades_por_embalagem,
  })
}

// A frase que aparece na tela, no PDF e na conferencia. Uma funcao so, para as
// tres nao divergirem -- ja aconteceu de duas telas do mesmo dado discordarem.
// ⚠ Esta funcao NAO le `embalagem.fator` -- so o nome. Quem decide se existe
// avulsa e o chamador, atraves de temEmbalagemFechada(); um produto sem
// embalagem fechada nunca chega aqui com avulsas > 0. Recebe a Embalagem
// inteira, e nao so o nome, para as tres telas chamarem do mesmo jeito.
export function textoDaEmbalagem(
  quantidade: number,
  avulsas: number,
  embalagem: Embalagem,
): string {
  const partes: string[] = []
  if (quantidade > 0 || avulsas === 0) partes.push(`${quantidade} ${embalagem.nome}`)
  if (avulsas > 0) partes.push(`${avulsas} un`)
  return partes.join(' + ')
}

// ⭐ Pedido dela em 17/09: ao lado da caixa, mostrar o peso da unidade
// (ex.: "12X400G"), nao so "(12 un)". Nao existe campo de peso no banco --
// os fornecedores costumam escrever isso no proprio NOME do produto
// ("Goiabada Cremosa 12X400G"), o mesmo padrao "NxPESO" que a importacao ja
// aprendeu a ler (validacao.ts, achado do mesmo dia). Exige que o numero do
// nome seja O MESMO fator do item vendido -- sem essa amarra, um numero
// qualquer perto no nome (codigo, validade) inventaria um peso errado.
export function pesoDaUnidadeNoNome(fator: number, nomeProduto: string): string | null {
  const m = nomeProduto.match(/(\d+)\s*[xX]\s*(\d+(?:[.,]\d+)?)\s*(kg|gr|g|l|ml)/i)
  if (!m || Number(m[1]) !== fator) return null
  return `${m[2]}${unidadeDoPeso(m[3])}`
}

// Peso em MINUSCULO (decisao dela, 24/09), na ficha E no pedido: "190g",
// "1,5kg", "30ml". "GR" vira "g". O "L" de litro fica maiusculo -- "1l" se
// confunde com "11" na tela pequena.
export function unidadeDoPeso(u: string): string {
  const U = u.toUpperCase()
  if (U === 'L') return 'L'
  return U === 'GR' ? 'g' : U.toLowerCase()
}

// ▶ Quanto este item vale EM UNIDADES, somando as duas dimensoes.
//
// E a unica medida com que se pode comparar "antes" e "depois" depois que a
// venda passou a ter caixa E unidade solta: contar so caixas deixa passar a
// troca de "1 caixa de 20" por "0 caixas + 40 avulsas", que e o DOBRO da venda
// entrando disfarcado de corte da fabrica -- a quantidade de caixas cai de 1
// para 0 e a guarda velha aplaude.
//
// `?? 1` no fator: item sem embalagem fechada tem uma unidade por unidade, e e
// tambem o que todo item gravado antes da 0021 le. `?? 0` nas avulsas pelo
// mesmo motivo de sempre -- `undefined` viraria NaN e NaN > NaN e falso, que
// desligaria a guarda em silencio no aparelho de quem nao sincronizou.
export function unidadesTotais(item: {
  quantidade: number
  quantidade_avulsa?: number | null
  unidades_por_embalagem?: number | null
}): number {
  return item.quantidade * (item.unidades_por_embalagem ?? 1) + (item.quantidade_avulsa ?? 0)
}
